import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
    LayoutDashboard,
    Network,
    Users2,
    Users,
    Home,
    FileText,
    LogOut,
    Menu,
    X,
    UserCircle,
    PlusCircle,
    LineChart,
    Zap,
    Key,
    MessageSquare,
    History,
    Heart,
    Activity
} from 'lucide-react';

const Navbar = () => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const userString = localStorage.getItem('milco_user');
    const user = userString ? JSON.parse(userString) : null;

    if (!user) return null;

    const handleLogout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (e) {
            console.error("Error logging out from server:", e);
        } finally {
            localStorage.removeItem('milco_token');
            localStorage.removeItem('milco_user');
            navigate('/');
        }
    };

    const hasAdminAccess = ['SuperAdmin', 'Admin', 'Pastor'].includes(user.cargo);
    const showDiscipulado = ['Coordinador', 'Pastor', 'SuperAdmin', 'Admin', 'Administrador', 'Supervisor', 'Lider'].includes(user.cargo);

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
        `group relative flex items-center gap-1.5 px-2.5 2xl:px-4 py-2 rounded-2xl text-[10px] 2xl:text-[11px] font-black uppercase tracking-[0.08em] 2xl:tracking-[0.15em] transition-all duration-500 ${isActive
            ? 'text-white bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.15)]'
            : 'text-slate-400 hover:text-white hover:bg-white/5'
        }`;

    return (
        <nav className="bg-[#0b1222] border-b border-white/5 sticky top-0 z-[100] backdrop-blur-xl">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
                <div className="flex items-center justify-between h-20">

                    <div className="flex items-center gap-12">
                        <NavLink to="/dashboard" className="flex items-center gap-4 active:scale-95 transition-transform group">
                            <div className="h-10 w-0.5 bg-white/10 rounded-full"></div>
                            <div className="flex flex-col relative">
                                <span className="text-white font-black text-[22px] tracking-tighter leading-none">
                                    MILCO<span className="text-amber-500">APP</span>
                                </span>
                                <span className="text-[9px] font-black text-amber-500/40 uppercase tracking-[0.4em] mt-0.5">Ministerios</span>
                            </div>
                        </NavLink>

                        {/* Desktop Menu */}
                        <div className="hidden xl:block">
                            <div className="flex items-center gap-0.5 2xl:gap-1">
                                <NavLink to="/dashboard" className={navLinkClass}>
                                    {({ isActive }) => (
                                        <>
                                            <LayoutDashboard size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                            <span>Dashboard</span>
                                            {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                        </>
                                    )}
                                </NavLink>

                                {hasAdminAccess ? (
                                    <>
                                        <NavLink to="/admin/miembros" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Users size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Miembros</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/admin/lideres" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Users2 size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Líderes</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/admin/organigrama" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Network size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Redes</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/admin/casas" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Home size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Casas</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/reportes" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <FileText size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Reportes</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/admin/usuarios" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Key size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Acceso</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        {['SuperAdmin', 'Admin', 'Administrador'].includes(user.cargo) && (
                                            <NavLink to="/admin/logs" className={navLinkClass}>
                                                {({ isActive }) => (
                                                    <>
                                                        <Activity size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                        <span>Logs</span>
                                                        {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full"></div>}
                                                    </>
                                                )}
                                            </NavLink>
                                        )}
                                        <NavLink to="/discipulado" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Heart size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Discipulado</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                    </>
                                ) : (
                                    <>
                                        <NavLink to="/nuevo-miembro" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <PlusCircle size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Nuevo</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/reporte-semanal" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <FileText size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Reporte</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/historial-reportes" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <History size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Historial</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/graficas" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <LineChart size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Gráficas</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        <NavLink to="/influencia" className={navLinkClass}>
                                            {({ isActive }) => (
                                                <>
                                                    <Zap size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                    <span>Influencia</span>
                                                    {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                </>
                                            )}
                                        </NavLink>
                                        {showDiscipulado && (
                                            <NavLink to="/discipulado" className={navLinkClass}>
                                                {({ isActive }) => (
                                                    <>
                                                        <Heart size={16} className={`${isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-amber-400'} transition-colors`} />
                                                        <span>Discipulado</span>
                                                        {isActive && <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-amber-500 rounded-full animate-in fade-in zoom-in duration-500"></div>}
                                                    </>
                                                )}
                                            </NavLink>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Section */}
                    <div className="hidden md:flex items-center gap-8">
                        <NavLink 
                            to="/chat" 
                            className={({ isActive }) => `relative group p-3 rounded-2xl transition-all ${isActive ? 'bg-amber-500/20 text-amber-500' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'}`}
                        >
                            <div className="absolute inset-0 bg-amber-500 rounded-2xl blur-md opacity-0 group-hover:opacity-20 transition-opacity"></div>
                            <MessageSquare size={24} className="relative z-10" />
                            {/* Indicador de mensajes no leídos (desactivado hasta tener lógica de conteo real) */}
                            {/* <div className="absolute top-2 right-2 h-2.5 w-2.5 bg-red-500 border-2 border-[#0b1222] rounded-full z-20"></div> */}
                        </NavLink>

                        <div className="w-px h-8 bg-white/5"></div>

                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-5 py-2.5 rounded-2xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300 font-black text-[10px] uppercase tracking-widest group"
                        >
                            <span>Salir</span>
                            <LogOut size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    {/* Mobile Button */}
                    <div className="xl:hidden flex items-center">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-3 rounded-2xl bg-white/5 text-white border border-white/10"
                        >
                            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <div className="xl:hidden absolute top-[80px] left-0 w-full bg-[#0b1222] border-b border-white/5 shadow-2xl animate-in slide-in-from-top duration-300 z-[90]">
                    <div className="px-6 pt-6 pb-12 space-y-3">
                        <NavLink to="/dashboard" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
                            {({ isActive }) => (
                                <>
                                    <LayoutDashboard size={20} className={isActive ? 'text-white' : 'text-amber-500'} />
                                    <span>Dashboard</span>
                                </>
                            )}
                        </NavLink>

                        {hasAdminAccess ? (
                            <>
                                <NavLink to="/admin/miembros" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Users size={20} />
                                    <span>Miembros</span>
                                </NavLink>
                                <NavLink to="/admin/lideres" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Users2 size={20} />
                                    <span>Líderes</span>
                                </NavLink>
                                <NavLink to="/admin/organigrama" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Network size={20} />
                                    <span>Redes</span>
                                </NavLink>
                                <NavLink to="/admin/casas" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Home size={20} />
                                    <span>Casas</span>
                                </NavLink>
                                <NavLink to="/reportes" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <FileText size={20} />
                                    <span>Reportes</span>
                                </NavLink>
                                <NavLink to="/admin/usuarios" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Key size={20} />
                                    <span>Acceso</span>
                                </NavLink>
                                {['SuperAdmin', 'Admin', 'Administrador'].includes(user.cargo) && (
                                    <NavLink to="/admin/logs" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                        <Activity size={20} />
                                        <span>Logs</span>
                                    </NavLink>
                                )}
                                <NavLink to="/discipulado" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Heart size={20} />
                                    <span>Discipulado</span>
                                </NavLink>
                                <NavLink to="/chat" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <MessageSquare size={20} />
                                    <span>Chat</span>
                                </NavLink>
                            </>
                        ) : (
                            <>
                                <NavLink to="/nuevo-miembro" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <PlusCircle size={20} />
                                    <span>Agregar Miembro</span>
                                </NavLink>
                                <NavLink to="/reporte-semanal" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <FileText size={20} />
                                    <span>Reporte Semanal</span>
                                </NavLink>
                                <NavLink to="/historial-reportes" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <History size={20} />
                                    <span>Historial Reportes</span>
                                </NavLink>
                                <NavLink to="/discipulado" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <Heart size={20} />
                                    <span>Discipulado</span>
                                </NavLink>
                                <NavLink to="/chat" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `flex items-center gap-4 p-4 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${isActive ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'hover:bg-white/5 text-slate-400 hover:text-white'}`}>
                                    <MessageSquare size={20} />
                                    <span>Chat</span>
                                </NavLink>
                            </>
                        )}

                        <div className="pt-8 mt-8 border-t border-white/5 flex items-center justify-between px-4">
                            <div className="flex items-center gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-amber-600/20 flex items-center justify-center text-amber-400 shadow-xl shadow-amber-900/40">
                                    <UserCircle size={28} />
                                </div>
                                <div>
                                    <p className="text-sm font-black text-white leading-none uppercase">{user.nombre}</p>
                                    <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest mt-2">{user.cargo}</p>
                                </div>
                            </div>
                            <button onClick={handleLogout} className="p-4 rounded-2xl bg-red-500/10 text-red-500 active:scale-95 transition-all">
                                <LogOut size={24} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
